import { AnalyticsUserEvent } from '@fruitful/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    type NativeSyntheticEvent,
    Platform,
    type TextInputContentSizeChangeEventData,
    type TextInputKeyPressEventData,
} from 'react-native';
import {
    ArrowUpIcon,
    Icon,
    IconButton,
    isNonMobileWeb,
    PencilIcon,
    SquareIcon,
    TamaguiInput,
    XStack,
    YStack,
} from 'smoothie';

import { track } from '../../../logger/Logger';
import { BaseInputWidget } from '../widgets/BaseInputWidget';
import NewChatButton from './NewChatButton';

export const CHAT_INPUT_DEFAULT_PLACEHOLDER = 'Reply to Fruitful...';
export const ONBOARDING_AI_DISCLOSURE =
    'Fruitful onboarding is driven by AI and can make mistakes.';
export const SPROUT_AI_DISCLOSURE =
    'Fruitful analytics is driven by AI and can make mistakes.';

const CHAT_INPUT_MOBILE_MAX_LINES = 9;
const CHAT_INPUT_DESKTOP_MAX_LINES = 16;
const CHAT_INPUT_LINE_HEIGHT = 24;
const CHAT_INPUT_VERTICAL_PADDING = 32;

const isMobileChatInput = (): boolean =>
    Platform.OS !== 'web' || !isNonMobileWeb;

const getChatInputMaxLines = (): number =>
    isMobileChatInput()
        ? CHAT_INPUT_MOBILE_MAX_LINES
        : CHAT_INPUT_DESKTOP_MAX_LINES;

const getChatInputMaxHeight = (): number =>
    getChatInputMaxLines() * CHAT_INPUT_LINE_HEIGHT +
    CHAT_INPUT_VERTICAL_PADDING;

const getCollapsedHeight = (): number =>
    (isMobileChatInput() ? 1 : 2) * CHAT_INPUT_LINE_HEIGHT +
    CHAT_INPUT_VERTICAL_PADDING;

const getChatInputCollapsedLines = (): number => (isMobileChatInput() ? 1 : 2);

const computeChatInputHeight = (
    text: string,
    contentHeight: number,
    collapsedHeight: number,
    maxHeight: number,
): number => {
    if (text === '') {
        return collapsedHeight;
    }

    const fromLineCount =
        Math.max(1, text.split('\n').length) * CHAT_INPUT_LINE_HEIGHT +
        CHAT_INPUT_VERTICAL_PADDING;
    const fromContent =
        Platform.OS === 'web'
            ? contentHeight
            : contentHeight + CHAT_INPUT_VERTICAL_PADDING;

    return Math.min(
        Math.max(fromLineCount, fromContent, collapsedHeight),
        maxHeight,
    );
};

const resetChatInputHorizontalScroll = (
    inputRef: React.RefObject<React.ElementRef<typeof TamaguiInput> | null>,
): void => {
    if (Platform.OS !== 'web') {
        return;
    }

    const node = inputRef.current as unknown as HTMLTextAreaElement | null;
    if (node != null && 'scrollLeft' in node) {
        node.scrollLeft = 0;
    }
};

interface ChatInputProps {
    onSend: (message: string) => void;
    onStop?: () => void;
    onReset?: () => void;
    showResetButton?: boolean;
    isStreaming?: boolean;
    disabled?: boolean;
    placeholder?: string;
    /** Removes bottom padding so a disclosure line's own top padding can sit flush below the input. */
    compactBottom?: boolean;
    /** Renders text input and send button on the same line. */
    inline?: boolean;
    /**
     * Shows a leading pencil icon (matches the "just an input" empty-state
     * design). Only rendered in the `inline` (one-line) layout.
     */
    showEditIcon?: boolean;
}

const ChatInputField = ({
    inputValue,
    onChangeText,
    placeholder,
    disabled,
    onKeyPress,
    focusOnEnable,
    inline,
}: {
    inputValue: string;
    onChangeText: (text: string) => void;
    placeholder: string;
    disabled: boolean;
    onKeyPress: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
    focusOnEnable?: boolean;
    inline?: boolean;
}) => {
    const collapsedHeight = getCollapsedHeight();
    const collapsedContentHeight =
        collapsedHeight - CHAT_INPUT_VERTICAL_PADDING;
    const collapsedLines = getChatInputCollapsedLines();
    const maxHeight = getChatInputMaxHeight();

    const inputRef = useRef<React.ElementRef<typeof TamaguiInput>>(null);
    const inputValueRef = useRef(inputValue);
    const previousContentHeightRef = useRef(collapsedContentHeight);
    const [inputHeight, setInputHeight] = useState(collapsedHeight);

    inputValueRef.current = inputValue;

    const wasDisabledRef = useRef(disabled);
    useEffect(() => {
        if (focusOnEnable && wasDisabledRef.current && !disabled) {
            inputRef.current?.focus();
        }
        wasDisabledRef.current = disabled;
    }, [disabled, focusOnEnable]);

    useEffect(() => {
        if (inputValue === '') {
            setInputHeight(collapsedHeight);
            previousContentHeightRef.current = collapsedContentHeight;
        }
    }, [collapsedContentHeight, collapsedHeight, inputValue]);

    const scheduleHorizontalScrollReset = useCallback(() => {
        requestAnimationFrame(() => {
            resetChatInputHorizontalScroll(inputRef);
        });
    }, []);

    // On mobile web the collapsed single-line textarea can briefly render
    // horizontally scrolled on mount, clipping the placeholder (e.g. "Reply
    // to Fruitful..." shows as just "Rep"). Reset it as soon as it mounts,
    // the same way we already do after text changes.
    useEffect(() => {
        scheduleHorizontalScrollReset();
    }, [scheduleHorizontalScrollReset]);

    const updateInputHeight = useCallback(
        (text: string, contentHeight: number) => {
            setInputHeight(
                computeChatInputHeight(
                    text,
                    contentHeight,
                    collapsedHeight,
                    maxHeight,
                ),
            );
        },
        [collapsedHeight, maxHeight],
    );

    const handleChangeText = useCallback(
        (text: string) => {
            inputValueRef.current = text;
            onChangeText(text);
            scheduleHorizontalScrollReset();
        },
        [onChangeText, scheduleHorizontalScrollReset],
    );

    // Web-only: RN Web reliably fires onContentSizeChange for the textarea, so
    // the field height is controlled from JS. Native uses RN's built-in
    // multiline auto-grow (minHeight/maxHeight) instead.
    const handleContentSizeChange = useCallback(
        (event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>) => {
            const contentHeight = Math.ceil(
                event.nativeEvent.contentSize.height,
            );
            const text = inputValueRef.current;

            if (text === '' && contentHeight <= collapsedContentHeight) {
                return;
            }

            if (contentHeight > previousContentHeightRef.current) {
                scheduleHorizontalScrollReset();
            }

            previousContentHeightRef.current = contentHeight;
            updateInputHeight(text, contentHeight);
        },
        [
            collapsedContentHeight,
            scheduleHorizontalScrollReset,
            updateInputHeight,
        ],
    );

    // Both platforms render the same TamaguiInput; only the height strategy is
    // branched.
    //   - Web: height is JS-controlled via inputHeight + onContentSizeChange,
    //     capped at maxHeight where it begins scrolling internally.
    //   - Native (iOS/Android): TamaguiInput's multiline size variant resolves
    //     height to 'auto' when numberOfLines/rows is omitted, so the field
    //     self-sizes between minHeight and maxHeight. Growth is handled natively
    //     (onContentSizeChange is suppressed on RN 0.79 iOS) and it scrolls once
    //     content exceeds maxHeight.
    const platformProps: Partial<React.ComponentProps<typeof TamaguiInput>> =
        Platform.OS === 'web'
            ? {
                  numberOfLines: collapsedLines,
                  height: inputHeight,
                  scrollEnabled: inputHeight >= maxHeight,
                  onContentSizeChange: handleContentSizeChange,
                  borderRadius: 24,
                  focusVisibleStyle: {
                      borderColor: 'transparent',
                      borderWidth: 0,
                      outlineWidth: 0,
                  },
                  '$platform-web': {
                      overflowX: 'hidden',
                      overflowWrap: 'break-word',
                      wordWrap: 'break-word',
                  },
              }
            : {
                  minHeight: collapsedHeight,
                  maxHeight,
              };

    return (
        <TamaguiInput
            ref={inputRef}
            multiline
            autoCapitalize="sentences"
            outlineWidth={0}
            value={inputValue}
            onChangeText={handleChangeText}
            onKeyPress={onKeyPress}
            disabled={disabled}
            placeholder={placeholder}
            placeholderTextColor="$gray800"
            width="100%"
            minWidth={0}
            paddingHorizontal={16}
            paddingVertical={16}
            fontSize="$4"
            fontWeight="$1"
            color="$black"
            borderWidth={0}
            borderColor="transparent"
            backgroundColor="transparent"
            textAlignVertical={inline ? 'center' : 'top'}
            hoverStyle={{
                borderColor: 'transparent',
                borderWidth: 0,
                outlineWidth: 0,
            }}
            focusStyle={{
                borderColor: 'transparent',
                borderWidth: 0,
                outlineWidth: 0,
            }}
            {...platformProps}
            {...(inline &&
                (Platform.OS === 'web'
                    ? isNonMobileWeb
                        ? { paddingVertical: 22, height: inputHeight - 12 }
                        : { scrollEnabled: false }
                    : {
                          paddingVertical: 22,
                          minHeight: CHAT_INPUT_LINE_HEIGHT + 44,
                          maxHeight: CHAT_INPUT_LINE_HEIGHT + 44,
                          scrollEnabled: false,
                      }))}
        />
    );
};

const ChatInputActions = ({
    isStreaming,
    disabled,
    inputValue,
    onSend,
    onStop,
    showResetButton,
    onReset,
    inline,
}: {
    isStreaming: boolean;
    disabled: boolean;
    inputValue: string;
    onSend: () => void;
    onStop?: () => void;
    showResetButton?: boolean;
    onReset?: () => void;
    inline?: boolean;
}) => {
    return (
        <XStack
            alignItems="center"
            justifyContent={showResetButton ? 'space-between' : 'flex-end'}
            paddingBottom={inline ? 0 : '$3'}
            paddingLeft={4}
            width={inline ? undefined : '100%'}
        >
            {showResetButton && (
                <NewChatButton onPress={onReset ?? (() => {})} />
            )}
            {isStreaming ? (
                <IconButton
                    variant="secondary"
                    icon={SquareIcon}
                    iconFill="$black"
                    onPress={() => onStop?.()}
                />
            ) : (
                <IconButton
                    variant="primary"
                    icon={ArrowUpIcon}
                    onPress={onSend}
                    disabled={disabled || inputValue.trim() === ''}
                />
            )}
        </XStack>
    );
};

export const ChatInput = ({
    onSend,
    onStop,
    onReset,
    showResetButton = false,
    isStreaming = false,
    disabled = false,
    placeholder = CHAT_INPUT_DEFAULT_PLACEHOLDER,
    compactBottom = false,
    inline = false,
    showEditIcon = false,
}: ChatInputProps) => {
    const [inputValue, setInputValue] = useState('');

    // `isStreaming` is async React state — it only reflects a send once a
    // render has happened, so a double-tap or Enter-key auto-repeat can
    // call handleSend twice in the same tick before that lands, firing
    // two independent sends for one message. This ref closes that window
    // synchronously; the effect keeps it in sync with isStreaming once
    // the real state catches up, so isStreaming stays the source of truth
    // for the normal (non-racy) "can't send while streaming" case.
    const sendingRef = useRef(false);
    useEffect(() => {
        sendingRef.current = isStreaming;
    }, [isStreaming]);

    const handleSend = useCallback(() => {
        const trimmedValue = inputValue.trim();
        if (sendingRef.current || disabled || isStreaming) {
            return;
        }
        if (trimmedValue !== '') {
            sendingRef.current = true;
            track(AnalyticsUserEvent.AI_CHAT_INPUT_TEXT_RESPONSE_SENT, {
                inputLength: trimmedValue.length,
            });
            onSend(trimmedValue);
            setInputValue('');
        }
    }, [disabled, inputValue, isStreaming, onSend]);

    const handleStop = useCallback(() => {
        track(AnalyticsUserEvent.AI_CHAT_INPUT_STOP_BUTTON_PRESSED);
        onStop?.();
    }, [onStop]);

    const handleKeyPress = useCallback(
        (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
            // for desktop web, enter key should submit the message
            // for mobile web/mobile app, enter key should add a new line
            if (Platform.OS !== 'web' || !isNonMobileWeb) {
                return;
            }

            const domEvent = (e as unknown as { nativeEvent: KeyboardEvent })
                .nativeEvent;

            if (domEvent.key === 'Enter') {
                if (domEvent.altKey) {
                    e.preventDefault();
                    setInputValue((prev) => `${prev}\n`);
                    return;
                }

                if (domEvent.shiftKey) {
                    return;
                }

                e.preventDefault();
                handleSend();
            }
        },
        [handleSend],
    );

    return (
        <YStack
            width="100%"
            paddingHorizontal="$4"
            paddingBottom={compactBottom ? 0 : '$6'}
            opacity={disabled ? 0.4 : 1}
        >
            <BaseInputWidget
                width="100%"
                borderRadius={24}
                paddingRight="$3"
                paddingBottom={0}
            >
                {inline ? (
                    <XStack width="100%" alignItems="center">
                        {showEditIcon && (
                            <XStack
                                width={40}
                                height={40}
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                            >
                                <Icon
                                    icon={PencilIcon}
                                    size={16}
                                    stringColor="#1a1a1a"
                                />
                            </XStack>
                        )}
                        <XStack flex={1} minWidth={0}>
                            <ChatInputField
                                inputValue={inputValue}
                                onChangeText={setInputValue}
                                placeholder={placeholder}
                                disabled={disabled || isStreaming}
                                onKeyPress={handleKeyPress}
                                focusOnEnable={isNonMobileWeb}
                                inline
                            />
                        </XStack>
                        <ChatInputActions
                            isStreaming={isStreaming}
                            disabled={disabled}
                            inputValue={inputValue}
                            onSend={handleSend}
                            onStop={handleStop}
                            showResetButton={showResetButton}
                            onReset={onReset}
                            inline
                        />
                    </XStack>
                ) : (
                    <>
                        <YStack minWidth={0}>
                            <ChatInputField
                                inputValue={inputValue}
                                onChangeText={setInputValue}
                                placeholder={placeholder}
                                disabled={disabled || isStreaming}
                                onKeyPress={handleKeyPress}
                                focusOnEnable={isNonMobileWeb}
                            />
                        </YStack>
                        <ChatInputActions
                            isStreaming={isStreaming}
                            disabled={disabled}
                            inputValue={inputValue}
                            onSend={handleSend}
                            onStop={handleStop}
                            showResetButton={showResetButton}
                            onReset={onReset}
                        />
                    </>
                )}
            </BaseInputWidget>
        </YStack>
    );
};
